import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import FeeBreakdown from '../components/FeeBreakdown';
import Footer from '../components/Footer';
import { useFees } from '../context/FeeContext';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Earnings = ({ user, onLogout }) => {
  const { t } = useTranslation();
  const { feeRates, refreshFeeRates } = useFees();
  const [financialSummary, setFinancialSummary] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  
  // User's registered payment methods
  const [userPaymentMethods, setUserPaymentMethods] = useState([]);
  // Enabled payment methods from owner config
  const [enabledPaymentMethods, setEnabledPaymentMethods] = useState({});
  // Filtered available methods (registered AND enabled)
  const [availablePaymentMethods, setAvailablePaymentMethods] = useState([]);

  useEffect(() => {
    fetchFinancialData();
    refreshFeeRates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Filter user's payment methods to only show those enabled by owner
    if (userPaymentMethods.length > 0 && Object.keys(enabledPaymentMethods).length > 0) {
      const filtered = userPaymentMethods.filter(method => {
        const methodType = (method.type || method.method_type)?.toLowerCase();
        const configKey = methodType === 'bank_transfer' ? 'bank_transfer' : 
                          methodType === 'gcash' ? 'gcash' :
                          methodType === 'maya' ? 'maya' :
                          methodType === 'card' ? 'card' : methodType;
        return enabledPaymentMethods[configKey] === true;
      });
      setAvailablePaymentMethods(filtered);
      
      // Auto-select first available method
      if (filtered.length > 0 && !selectedPaymentMethod) {
        setSelectedPaymentMethod(filtered[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userPaymentMethods, enabledPaymentMethods]);

  const fetchFinancialData = async () => {
    try {
      const [summaryRes, transactionsRes, withdrawalsRes, paymentMethodsRes, payoutConfigRes] = await Promise.all([
        axios.get(`${API}/financial/summary`),
        axios.get(`${API}/transactions?role=provider`),
        axios.get(`${API}/withdrawals`),
        axios.get(`${API}/payment-methods`),
        axios.get(`${API}/payout-methods/enabled`)
      ]);

      setFinancialSummary(summaryRes.data);
      setTransactions(transactionsRes.data);
      setWithdrawals(withdrawalsRes.data);
      setUserPaymentMethods(paymentMethodsRes.data || []);
      // Withdrawals are PAYOUTS — gate by the payout config, not the client
      // checkout config (a method disabled for checkout can still be a payout target).
      setEnabledPaymentMethods(payoutConfigRes.data?.enabled_payout_methods || {});
    } catch (error) {
      console.error('Error fetching financial data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawalRequest = async (e) => {
    e.preventDefault();
    
    if (!selectedPaymentMethod) {
      alert(t('earnings.selectMethodAlert'));
      return;
    }
    
    setSubmitting(true);

    try {
      await axios.post(`${API}/withdrawals`, {
        amount: parseFloat(withdrawAmount),
        bank_account: `${selectedPaymentMethod.name || selectedPaymentMethod.account_name} - ${selectedPaymentMethod.account_number}`,
        payment_method: selectedPaymentMethod.type || selectedPaymentMethod.method_type,
        payment_method_id: selectedPaymentMethod.id
      });
      alert(t('earnings.withdrawSuccess'));
      setShowWithdrawModal(false);
      setWithdrawAmount('');
      setSelectedPaymentMethod(availablePaymentMethods[0] || null);
      fetchFinancialData();
    } catch (error) {
      alert(error.response?.data?.detail || t('earnings.withdrawFail'));
    } finally {
      setSubmitting(false);
    }
  };

  const getMethodIcon = (methodType) => {
    switch (methodType?.toLowerCase()) {
      case 'gcash': return '💰';
      case 'maya': return '🏦';
      case 'bank_transfer': return '🏛️';
      case 'card': return '💳';
      default: return '💵';
    }
  };

  const getMethodLabel = (methodType) => {
    switch (methodType?.toLowerCase()) {
      case 'gcash': return 'GCash';
      case 'maya': return 'Maya';
      case 'bank_transfer': return 'Bank Transfer';
      case 'card': return 'Card';
      default: return methodType;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar user={user} onLogout={onLogout} />
        <div className="flex items-center justify-center h-96">
          <div className="text-xl font-semibold text-gray-600">{t('earnings.loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="earnings-page">
      <Navbar user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="text-3xl font-bold mb-8 bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent" data-testid="page-title">{t('earnings.title')}</h1>

        {/* Financial Summary Cards */}
        {financialSummary && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="financial-summary">
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
              <h3 className="text-gray-500 text-sm font-medium">{t('earnings.availableBalance')}</h3>
              <p className="text-3xl font-bold text-green-600 mt-2">
                {financialSummary.currency} {financialSummary.available_balance.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 mt-1">{t('earnings.readyToWithdraw')}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
              <h3 className="text-gray-500 text-sm font-medium">{t('earnings.totalEarnings')}</h3>
              <p className="text-3xl font-bold text-blue-600 mt-2">
                {financialSummary.currency} {financialSummary.total_earnings.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 mt-1">{t('earnings.lifetimeEarnings')}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-500">
              <h3 className="text-gray-500 text-sm font-medium">{t('earnings.totalWithdrawn')}</h3>
              <p className="text-3xl font-bold text-purple-600 mt-2">
                {financialSummary.currency} {financialSummary.total_withdrawn.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 mt-1">{t('earnings.successfullyWithdrawn')}</p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-yellow-500">
              <h3 className="text-gray-500 text-sm font-medium">{t('earnings.pendingWithdrawals')}</h3>
              <p className="text-3xl font-bold text-yellow-600 mt-2">
                {financialSummary.currency} {financialSummary.pending_withdrawals.toFixed(2)}
              </p>
              <p className="text-xs text-gray-500 mt-1">{t('earnings.beingProcessed')}</p>
            </div>
          </div>
        )}

        {/* Action Button */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-xl font-bold">{t('earnings.requestWithdrawal')}</h2>
              <p className="text-gray-600 mt-1">{t('earnings.withdrawDesc')}</p>
            </div>
            <button
              onClick={() => setShowWithdrawModal(true)}
              disabled={!financialSummary || financialSummary.available_balance <= 0}
              className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-3 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition"
              data-testid="request-withdrawal-button"
            >
              {t('earnings.requestWithdrawal')}
            </button>
          </div>
        </div>

        {/* Commission Info */}
        <div className="bg-gradient-to-r from-orange-50 to-orange-100 p-6 rounded-lg mb-8 border border-blue-200">
          <div className="flex items-start gap-4">
            <div className="bg-white p-3 rounded-full">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
              </svg>
            </div>
            <div>
              <h3 className="font-bold text-lg mb-2">{t('earnings.commissionStructure')}</h3>
              <p className="text-gray-700 mb-2">{t('earnings.commissionIntro')}</p>
              <ul className="list-disc list-inside text-gray-700 space-y-1">
                <li><strong>{100 - feeRates.provider_fee_rate}%</strong> {t('earnings.goesToYou')}</li>
                <li><strong>{feeRates.provider_fee_rate}%</strong> {t('earnings.platformCommission')}</li>
              </ul>
              <p className="text-sm text-gray-600 mt-3">{t('earnings.commissionNote')}</p>
              <FeeBreakdown rate={feeRates.provider_fee_rate} />
            </div>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-white p-6 rounded-lg shadow-md mb-8" data-testid="transactions-section">
          <h2 className="text-xl font-bold mb-6">{t('earnings.recentTransactions')}</h2>
          {transactions.length === 0 ? (
            <p className="text-gray-500 text-center py-8">{t('earnings.noTransactions')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('earnings.date')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('earnings.client')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('earnings.totalAmount')}</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('earnings.yourEarnings')} ({100 - feeRates.provider_fee_rate}%)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('earnings.platformFee')} ({feeRates.provider_fee_rate}%)</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">{t('earnings.status')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {transactions.map((txn) => (
                    <tr key={txn.id} data-testid={`transaction-${txn.id}`}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {new Date(txn.created_at).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{txn.payer_name || txn.client_name}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                        {txn.currency} {txn.amount?.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                        {txn.currency} {txn.provider_amount?.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {txn.currency} {txn.platform_commission?.toFixed(2)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          {t('earnings.statuses.' + txn.status, txn.status)}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Withdrawal History */}
        <div className="bg-white p-6 rounded-lg shadow-md" data-testid="withdrawals-section">
          <h2 className="text-xl font-bold mb-6">{t('earnings.withdrawalHistory')}</h2>
          {withdrawals.length === 0 ? (
            <p className="text-gray-500 text-center py-8">{t('earnings.noWithdrawals')}</p>
          ) : (
            <div className="space-y-4">
              {withdrawals.map((withdrawal) => (
                <div key={withdrawal.id} className="border rounded-lg p-4" data-testid={`withdrawal-${withdrawal.id}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-bold text-lg">{withdrawal.currency} {withdrawal.amount.toFixed(2)}</p>
                      <p className="text-sm text-gray-600">{t('earnings.requestedOn')} {new Date(withdrawal.created_at).toLocaleDateString()}</p>
                      <p className="text-sm text-gray-600">{t('earnings.method')}: {getMethodLabel(withdrawal.payment_method)}</p>
                      <p className="text-sm text-gray-600">{t('earnings.account')}: {withdrawal.bank_account}</p>
                      {withdrawal.status === 'paid' && (
                        <p className="text-sm text-green-700 mt-1">
                          {t('earnings.paidOn')} {withdrawal.paid_at ? new Date(withdrawal.paid_at).toLocaleDateString() : '—'}
                          {withdrawal.reference_number && <span className="font-mono"> · {t('earnings.ref')}: {withdrawal.reference_number}</span>}
                        </p>
                      )}
                      {withdrawal.status === 'approved' && (
                        <p className="text-sm text-blue-600 mt-1">{t('earnings.approvedMsg')}</p>
                      )}
                      {withdrawal.notes && <p className="text-sm text-gray-500 mt-1 italic">{withdrawal.notes}</p>}
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      withdrawal.status === 'paid' || withdrawal.status === 'completed' ? 'bg-green-100 text-green-700' :
                      withdrawal.status === 'pending' ? 'bg-yellow-100 text-yellow-700' :
                      withdrawal.status === 'approved' ? 'bg-blue-100 text-blue-700' :
                      'bg-red-100 text-red-700'
                    }`}>
                      {withdrawal.status === 'approved' ? t('earnings.statuses.processing') : t('earnings.statuses.' + withdrawal.status, withdrawal.status)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Withdrawal Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" data-testid="withdrawal-modal">
          <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">{t('earnings.requestWithdrawal')}</h2>
            
            {availablePaymentMethods.length === 0 ? (
              <div className="text-center py-6">
                <div className="text-5xl mb-4">💳</div>
                <h3 className="text-lg font-semibold mb-2">{t('earnings.noMethodsTitle')}</h3>
                <p className="text-gray-600 mb-4">
                  {userPaymentMethods.length === 0 
                    ? t('earnings.noMethodsRegistered')
                    : t('earnings.noMethodsEnabled')}
                </p>
                <Link
                  to="/payment-methods"
                  className="inline-block bg-gradient-to-r from-orange-500 to-orange-600 text-white px-6 py-2 rounded-lg hover:shadow-lg font-semibold"
                >
                  {t('earnings.addPaymentMethod')}
                </Link>
                <button
                  type="button"
                  onClick={() => setShowWithdrawModal(false)}
                  className="block w-full mt-4 text-gray-600 hover:text-gray-800"
                >
                  {t('earnings.cancel')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleWithdrawalRequest}>
                {/* Amount Input */}
                <div className="mb-4">
                  <label className="block text-gray-700 font-semibold mb-2">{t('earnings.amount')}</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-gray-500">{financialSummary?.currency}</span>
                    <input
                      type="number"
                      step="0.01"
                      max={financialSummary?.available_balance}
                      value={withdrawAmount}
                      onChange={(e) => setWithdrawAmount(e.target.value)}
                      className="w-full pl-16 pr-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                      required
                      data-testid="withdrawal-amount-input"
                    />
                  </div>
                  <p className="text-sm text-gray-500 mt-1">
                    {t('earnings.available')}: {financialSummary?.currency} {financialSummary?.available_balance.toFixed(2)}
                  </p>
                </div>

                {/* Payment Method Selection */}
                <div className="mb-6">
                  <label className="block text-gray-700 font-semibold mb-2">{t('earnings.selectPaymentMethod')}</label>
                  <div className="space-y-3">
                    {availablePaymentMethods.map((method) => (
                      <div
                        key={method.id}
                        onClick={() => setSelectedPaymentMethod(method)}
                        className={`border rounded-lg p-4 cursor-pointer transition ${
                          selectedPaymentMethod?.id === method.id 
                            ? 'border-orange-500 bg-orange-50 ring-2 ring-orange-500' 
                            : 'border-gray-200 hover:border-orange-300'
                        }`}
                        data-testid={`payment-method-option-${method.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-2xl">{getMethodIcon(method.type || method.method_type)}</span>
                            <div>
                              <p className="font-semibold">{getMethodLabel(method.type || method.method_type)}</p>
                              <p className="text-sm text-gray-600">{method.name || method.account_name}</p>
                              <p className="text-sm text-gray-500">{method.account_number}</p>
                            </div>
                          </div>
                          {selectedPaymentMethod?.id === method.id && (
                            <svg className="w-6 h-6 text-orange-500" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                        {method.is_default && (
                          <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded mt-2 inline-block">{t('earnings.default')}</span>
                        )}
                      </div>
                    ))}
                  </div>
                  <Link
                    to="/payment-methods"
                    className="inline-block text-orange-600 hover:underline text-sm mt-3"
                  >
                    {t('earnings.addNewMethod')}
                  </Link>
                </div>

                {/* Selected Method Summary */}
                {selectedPaymentMethod && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg border">
                    <p className="text-sm text-gray-600 mb-1">{t('earnings.fundsSentTo')}</p>
                    <p className="font-semibold">{getMethodIcon(selectedPaymentMethod.type || selectedPaymentMethod.method_type)} {getMethodLabel(selectedPaymentMethod.type || selectedPaymentMethod.method_type)}</p>
                    <p className="text-gray-700">{selectedPaymentMethod.name || selectedPaymentMethod.account_name}</p>
                    <p className="text-gray-700">{selectedPaymentMethod.account_number}</p>
                  </div>
                )}

                <div className="flex gap-4">
                  <button
                    type="button"
                    onClick={() => setShowWithdrawModal(false)}
                    className="flex-1 bg-gray-200 text-gray-700 py-2 rounded-lg hover:bg-gray-300 font-semibold"
                    data-testid="cancel-button"
                  >
                    {t('earnings.cancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={submitting || !selectedPaymentMethod || !withdrawAmount}
                    className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 text-white py-2 rounded-lg hover:shadow-lg font-semibold disabled:opacity-50"
                    data-testid="submit-withdrawal-button"
                  >
                    {submitting ? t('earnings.submitting') : t('earnings.submitRequest')}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Earnings;
